import { Button, Grid, Typography } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import TextField from '@mui/material/TextField'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import CustomLoading from '../../components/custom/custom-loading'
import PublicWrapper from '../../components/public-wrapper-v2/public-wrapper'
import STATIC_TEXT from '../../content/common.json'
import { triggerCompleteRegistrationInvite } from './adf-screener-sink'
import { setSnackBar } from '../../stores/ui/ui-action'
import { setUserAction } from '../../stores/user/user-action'
import CONSTANTS from '../../utils/constants'
import useInUsaStyles from './adf-screener-in-usa-page.styles'
import {
    checkIfCompanyNameIsNotAcceptable,
    checkIfShouldDisableContinueButton,
    determineIfUsaCompany,
    getCompanyNameHelperText
} from './adf-screener-info-manager'
import Radiogroup from './radiogroup'

export default function AdfScreenerInUsaPage() {
    const user = useSelector((state: any) => state.user)
    const dispatch = useDispatch()
    const styleClasses = useInUsaStyles()
    const [isDisabled, setDisabled] = useState(true)
    const [companyName, setCompanyName] = useState(user.companyName ?? '')
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [usaCompanySelection, setUsaCompanySelection] = useState<string>(determineIfUsaCompany(user.countryOfIncorporation))
    const [companyNameIsNotAcceptable, setCompanyNameIsNotAcceptable] = useState(false)
    const navigate = useNavigate()

    function handleRadioChange(event) {
        setUsaCompanySelection(event.target.value)
    }

    // Note: causes a 'not mounted' warning while testing, but these warnings now get
    // ignored in React 18+
    function handleInputChange(event) {
        setCompanyName(event.target.value)
    }

    async function handleSubmit() {
        if (usaCompanySelection === '') return

        if (usaCompanySelection === 'No') {
            dispatch(setUserAction({ ...user, companyName, countryOfIncorporation: 'notUSA' }))
            navigate('/in-usa/error')
            return
        }

        setSubmitting(true)
        const updatedUser = { ...user, companyName, countryOfIncorporation: 'US', isNewCompany: true }
        dispatch(setUserAction(updatedUser))

        try {
            await triggerCompleteRegistrationInvite(updatedUser)
        } catch (err) {
            setSubmitting(false)
            dispatch(setSnackBar({ show: true, message: 'We apologize, the request failed. Please submit again.', messageType: 'error' }))
            return
        }

        setSubmitting(false)
        navigate('/verifyemail')
    }

    useEffect(() => {
        const nameStatus = checkIfCompanyNameIsNotAcceptable(companyName)
        setCompanyNameIsNotAcceptable(nameStatus)
    }, [companyName])

    useEffect(() => {
        const disableStatus = checkIfShouldDisableContinueButton(companyName, usaCompanySelection)
        setDisabled(disableStatus)
    }, [companyName, usaCompanySelection])

    return (
        <div className={styleClasses.trapezoidBgInUsa}>
            {submitting && <CustomLoading message="Loading..." />}
            <PublicWrapper
                data-testid="public-wrapper"
                title={STATIC_TEXT.inUsa.title}
                displayPrivacyPolicyFooter={true}
                backgroundClass={styleClasses.root}
                contentWrapperClass={styleClasses.inUsaWrapper}
            >
                <div className={styleClasses.formWrapper}>
                    <Typography variant="h1" className={styleClasses.title}>
                        Two more quick things...
                    </Typography>
                    <div className={styleClasses.screeningForm}>
                        <section className={styleClasses.questionGroup}>
                            <Typography component="h2" className={styleClasses.questionTitle}>
                                What is the name of your company?
                            </Typography>
                            <TextField
                                id="company-name"
                                label="Doing business as/trade name"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={companyName}
                                onChange={handleInputChange}
                                className={styleClasses.inputCompanyName}
                                error={companyNameIsNotAcceptable}
                                helperText={getCompanyNameHelperText(companyNameIsNotAcceptable)}
                            />
                        </section>
                        <section className={styleClasses.questionGroup}>
                            <Typography component="h2" className={styleClasses.questionTitle}>
                                {STATIC_TEXT.inUsa.radioLabel}
                            </Typography>
                            <div className={styleClasses.radio}>
                                <Radiogroup
                                    name="businessInUsa"
                                    label="businessInUsa"
                                    uiRenderOptions={{ row: false }}
                                    items={[
                                        {
                                            label: CONSTANTS.YES,
                                            value: CONSTANTS.YES
                                        },
                                        {
                                            label: CONSTANTS.NO,
                                            value: CONSTANTS.NO
                                        }
                                    ]}
                                    value={usaCompanySelection}
                                    onChange={handleRadioChange}
                                />
                            </div>
                        </section>
                        <Button className={styleClasses.continueBtn} disabled={isDisabled} onClick={handleSubmit}>
                            Continue
                        </Button>
                    </div>
                </div>
            </PublicWrapper>
        </div>
    )
}
